import Phaser from 'phaser';
import itemsData from '@/data/items.json';

interface ItemTplMin { id: number; icon?: string; }
const ITEM_TPL: Record<number, ItemTplMin> = Object.fromEntries(
  (itemsData.items as ItemTplMin[]).map((i) => [i.id, i]),
);

export interface EquipPileOpts {
  scene: Phaser.Scene;
  id: number;
  x: number;
  y: number;
  templateId: number;
  upgrade: number;
  label: string;
  iconColor: number;
  ownerUid?: string;
  ownerUntil?: number;
}

// EquipPile - dropped wearable item shown on the field. Glows in a gear-tier color
// (matches inventory iconColor mapping) and shows the item name + upgrade level.
export class EquipPile extends Phaser.GameObjects.Container {
  pileId: number;
  templateId: number;
  upgrade: number;
  ownerUid?: string;
  ownerUntil?: number;
  alive = true;
  private gfx!: Phaser.GameObjects.Graphics;
  private iconSprite?: Phaser.GameObjects.Image;
  private nameLabel!: Phaser.GameObjects.Text;
  private bobTween?: Phaser.Tweens.Tween;

  constructor(opts: EquipPileOpts) {
    super(opts.scene, opts.x, opts.y);
    this.pileId = opts.id;
    this.templateId = opts.templateId;
    this.upgrade = opts.upgrade;
    this.ownerUid = opts.ownerUid;
    this.ownerUntil = opts.ownerUntil;

    this.gfx = opts.scene.add.graphics();

    const tpl = ITEM_TPL[opts.templateId];
    const iconKey = tpl?.icon ? `item_${tpl.icon}` : null;

    if (iconKey && opts.scene.textures.exists(iconKey)) {
      this.iconSprite = opts.scene.add.image(0, 0, iconKey)
        .setDisplaySize(24, 24);
      this.drawShadow();
    } else {
      this.drawIcon(opts.iconColor);
    }

    const display = opts.upgrade > 0 ? `${opts.label} +${opts.upgrade}` : opts.label;
    this.nameLabel = opts.scene.add.text(0, -22, display, {
      fontFamily: '"Philosopher", "Cinzel", serif', fontSize: '11px',
      color: opts.upgrade > 0 ? '#ffeb3b' : '#cfd8dc',
      stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5);

    this.add([this.gfx, ...(this.iconSprite ? [this.iconSprite] : []), this.nameLabel]);
    this.setSize(40, 40);
    this.setInteractive(new Phaser.Geom.Rectangle(-20, -20, 40, 40), Phaser.Geom.Rectangle.Contains);
    this.setDepth(15);
    opts.scene.add.existing(this);

    this.bobTween = opts.scene.tweens.add({
      targets: this.iconSprite ?? this.gfx,
      y: -4,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private drawShadow() {
    this.gfx.clear();
    this.gfx.fillStyle(0x000000, 0.4);
    this.gfx.fillEllipse(0, 6, 18, 6);
  }

  private drawIcon(color: number) {
    this.gfx.clear();
    // shadow
    this.gfx.fillStyle(0x000000, 0.4);
    this.gfx.fillEllipse(0, 6, 18, 6);
    // glowing diamond
    this.gfx.fillStyle(color, 1);
    this.gfx.fillTriangle(0, -10, 9, 0, -9, 0);
    this.gfx.fillTriangle(-9, 0, 9, 0, 0, 10);
    this.gfx.lineStyle(1.5, 0xffffff, 0.8);
    this.gfx.strokeTriangle(0, -10, 9, 0, -9, 0);
    this.gfx.strokeTriangle(-9, 0, 9, 0, 0, 10);
    // shine
    this.gfx.fillStyle(0xffffff, 0.7);
    this.gfx.fillCircle(-3, -3, 2);
  }

  fadeOutAndDestroy() {
    if (!this.alive) return;
    this.alive = false;
    this.bobTween?.stop();
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      y: this.y - 16,
      duration: 250,
      onComplete: () => this.destroy(),
    });
  }
}
